import { useState, useCallback } from 'react'
import { z } from 'zod'
import { useValuationStore } from '@/store/valuationStore'
import { useAttributeStore } from '@/store/attributeStore'
import { useRefStore } from '@/store/refStore'
import type { RefOption } from '@/services/refService'
import { SECTIONS } from '@/data/sections.data'
import {
  TRUCK_STANDARD_FIELDS,
  TRAILER_STANDARD_FIELDS,
  TRUCK_ADDITIONAL_FIELDS,
  TRAILER_ADDITIONAL_FIELDS,
  TRUCK_CONDITIONAL_FIELDS,
  TRAILER_CONDITIONAL_FIELDS,
  TRUCK_USE_SUBTYPE_FIELD,
  TRAILER_TYPE_SUBTYPE_FIELD,
} from '@/data/conditionalFields.data'
import { normaliseToKey } from '@/data/refCategories'
import { mapAttributesToFields } from '@/utils/attributeMapper'
import { FIELD_VALIDATORS } from '@/utils/fieldValidators'
import type { AttributeDefinition } from '@/services/attributeService'
import type { FieldDefinition } from '@/types'

interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

/**
 * Validates a single section's form values against a Zod schema.
 * Returns per-field error messages and an overall validity flag.
 */
export function useFormValidation<T extends z.ZodRawShape>(
  sectionId: string,
  schema: z.ZodObject<T>
): {
  errors: Record<string, string>
  validate: () => ValidationResult
  clearErrors: () => void
} {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { formValues } = useValuationStore()

  const validate = useCallback((): ValidationResult => {
    const sectionValues = formValues[sectionId] ?? {}
    const result = schema.safeParse(sectionValues)

    if (result.success) {
      setErrors({})
      return { isValid: true, errors: {} }
    }

    const fieldErrors: Record<string, string> = {}
    for (const issue of result.error.issues) {
      const key = issue.path[0] as string
      if (key && !fieldErrors[key]) {
        fieldErrors[key] = issue.message
      }
    }

    setErrors(fieldErrors)
    return { isValid: false, errors: fieldErrors }
  }, [formValues, sectionId, schema])

  const clearErrors = useCallback(() => setErrors({}), [])

  return { errors, validate, clearErrors }
}

/**
 * Resolves the visible fields for every section based on current form values,
 * mirroring the logic in useConditionalFields so validation only targets
 * fields that are actually rendered.
 */
function resolveAllSectionFields(
  formValues: ReturnType<typeof useValuationStore.getState>['formValues'],
  attrCache: Record<string, AttributeDefinition[]>,
  refCache: Record<string, RefOption[]>
): Record<string, FieldDefinition[]> {
  const vehicleType = formValues['basic-information']?.['vehicleType'] ?? ''
  const useValue    = formValues['standard-equipment']?.['use']         ?? ''
  const typeValue   = formValues['standard-equipment']?.['type']        ?? ''
  const useSubtypeValue  = formValues['standard-equipment']?.['useSubtype']  ?? ''
  const typeSubtypeValue = formValues['standard-equipment']?.['typeSubtype'] ?? ''

  const isValidUse = !!useValue && useValue !== 'Select'
  const isValidType = !!typeValue && typeValue !== 'Select'
  // Cache presence (even []) means the live subtype lookup has completed; null = pending.
  const useSubtypeOptions = isValidUse ? refCache[`TRUCK_USE_SUBTYPE_${normaliseToKey(useValue)}`] : undefined
  const typeSubtypeOptions = isValidType ? refCache[`TRAILER_TYPE_SUBTYPE_${normaliseToKey(typeValue)}`] : undefined
  const hasUseSubtypes = useSubtypeOptions === undefined ? null : useSubtypeOptions.length > 0
  const hasTypeSubtypes = typeSubtypeOptions === undefined ? null : typeSubtypeOptions.length > 0

  let bodyFields: FieldDefinition[] = []
  if (vehicleType === 'Truck/Tractor' && isValidUse) {
    if (hasUseSubtypes === true) {
      const subtypeField: FieldDefinition = {
        ...TRUCK_USE_SUBTYPE_FIELD,
        refCategory: `TRUCK_USE_SUBTYPE_${normaliseToKey(useValue)}`,
      }
      const isValidUseSubtype = useSubtypeValue && useSubtypeValue !== 'Select'
      const category = `TRUCK_BODY_${normaliseToKey(useValue)}_${normaliseToKey(useSubtypeValue)}`
      const attrs = isValidUseSubtype ? attrCache[category] : undefined
      bodyFields = [subtypeField, ...(attrs?.length ? mapAttributesToFields(attrs) : [])]
    } else if (hasUseSubtypes === false) {
      const attrs = attrCache[`TRUCK_BODY_${normaliseToKey(useValue)}`]
      bodyFields = attrs?.length ? mapAttributesToFields(attrs) : []
    }
  } else if (vehicleType === 'Trailer' && isValidType) {
    if (hasTypeSubtypes === true) {
      const subtypeField: FieldDefinition = {
        ...TRAILER_TYPE_SUBTYPE_FIELD,
        refCategory: `TRAILER_TYPE_SUBTYPE_${normaliseToKey(typeValue)}`,
      }
      const isValidTypeSubtype = typeSubtypeValue && typeSubtypeValue !== 'Select'
      const category = `TRAILER_BODY_${normaliseToKey(typeValue)}_${normaliseToKey(typeSubtypeValue)}`
      const attrs = isValidTypeSubtype ? attrCache[category] : undefined
      bodyFields = [subtypeField, ...(attrs?.length ? mapAttributesToFields(attrs) : [])]
    } else if (hasTypeSubtypes === false) {
      const attrs = attrCache[`TRAILER_BODY_${normaliseToKey(typeValue)}`]
      bodyFields = attrs?.length ? mapAttributesToFields(attrs) : []
    }
  }

  const standardFields =
    vehicleType === 'Truck/Tractor' ? TRUCK_STANDARD_FIELDS :
    vehicleType === 'Trailer'       ? TRAILER_STANDARD_FIELDS :
    []

  return {
    'basic-information': SECTIONS.find(s => s.id === 'basic-information')?.fields ?? [],

    'standard-equipment': standardFields,

    'additional-options':
      vehicleType === 'Truck/Tractor' ? TRUCK_ADDITIONAL_FIELDS :
      vehicleType === 'Trailer'       ? TRAILER_ADDITIONAL_FIELDS :
      [],

    'body-information': bodyFields,

    'vehicle-condition':
      vehicleType === 'Truck/Tractor' ? TRUCK_CONDITIONAL_FIELDS :
      vehicleType === 'Trailer'       ? TRAILER_CONDITIONAL_FIELDS :
      [],
  }
}

export interface SectionValidationError {
  section: string
  fields: string[]
}

/** Reads a field's value, honoring FieldDefinition.formSection (see conditionalFields.data.ts). */
function fieldValue(
  formValues: ReturnType<typeof useValuationStore.getState>['formValues'],
  sectionId: string,
  f: FieldDefinition
): string {
  return formValues[f.formSection ?? sectionId]?.[f.id] ?? ''
}

/**
 * Validates format errors across all visible fields before saving a draft.
 * Only checks fields that already have a value — missing required fields are
 * intentionally allowed since partial drafts are a valid use case.
 * Returns per-section objects listing the label of each field with a format error.
 */
export function useSaveValidation(): () => SectionValidationError[] {
  const { formValues } = useValuationStore()
  const attrCache = useAttributeStore((s) => s.cache)
  const refCache = useRefStore((s) => s.cache)

  return useCallback((): SectionValidationError[] => {
    const sectionFields = resolveAllSectionFields(formValues, attrCache, refCache)
    const invalid: SectionValidationError[] = []

    const SAVE_REQUIRED_FIELDS = new Set(['claimRefNo', 'vehicleType'])

    for (const [sectionId, fields] of Object.entries(sectionFields)) {
      const vals = formValues[sectionId] ?? {}

      const missingLabels = fields
        .filter(f => SAVE_REQUIRED_FIELDS.has(f.id))
        .filter(f => {
          const v = fieldValue(formValues, sectionId, f)
          return !v || v.startsWith('Select') || v.trim() === ''
        })
        .map(f => f.label)

      const formatErrorLabels = fields
        .filter(f => {
          const v = fieldValue(formValues, sectionId, f)
          return v && v.trim() !== '' && !v.startsWith('Select')
        })
        .filter(f => !!FIELD_VALIDATORS[f.id]?.(fieldValue(formValues, sectionId, f), vals))
        .map(f => f.label)
        .filter(label => !missingLabels.includes(label))

      const allErrorLabels = [...missingLabels, ...formatErrorLabels]
      if (allErrorLabels.length > 0) {
        const section = sectionId
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
        invalid.push({ section, fields: allErrorLabels })
      }
    }

    return invalid
  }, [formValues, attrCache, refCache])
}

/**
 * Validates all required fields across the entire form before submission.
 * Only validates fields that are currently visible (driven by vehicleType,
 * use, and type selections), so conditional sections are never validated
 * when they are not applicable.
 * Returns per-section objects listing the label of each missing required field.
 */
export function useSubmitValidation(): () => SectionValidationError[] {
  const { formValues } = useValuationStore()
  const attrCache = useAttributeStore((s) => s.cache)
  const refCache = useRefStore((s) => s.cache)

  return useCallback((): SectionValidationError[] => {
    const sectionFields = resolveAllSectionFields(formValues, attrCache, refCache)
    const incomplete: SectionValidationError[] = []

    for (const [sectionId, fields] of Object.entries(sectionFields)) {
      const vals = formValues[sectionId] ?? {}
      const missingLabels = fields
        .filter(f => f.required)
        .filter(f => {
          const v = fieldValue(formValues, sectionId, f)
          return !v || v.startsWith('Select') || v.trim() === ''
        })
        .map(f => f.label)

      // Also collect fields with format errors (validator returns a message)
      const formatErrorLabels = fields
        .filter(f => {
          const v = fieldValue(formValues, sectionId, f)
          return !f.required || (v && !v.startsWith('Select'))
        })
        .filter(f => !!FIELD_VALIDATORS[f.id]?.(fieldValue(formValues, sectionId, f), vals))
        .map(f => f.label)
        .filter(label => !missingLabels.includes(label))

      const allErrorLabels = [...missingLabels, ...formatErrorLabels]
      if (allErrorLabels.length > 0) {
        const section = sectionId
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
        incomplete.push({ section, fields: allErrorLabels })
      }
    }

    return incomplete
  }, [formValues, attrCache, refCache])
}
